import { useMemo } from "react"
import { LexicalText } from "@/schema"
import { Button, Card } from "@ui/shad"
import { cn } from "@ui/utils"
import { Check, StickyNote } from "lucide-react"

import { useActionLink, useExecuteActionLink } from "@/hooks/api/action-links"
import { markdownToNodes } from "@/components/lexical/serializer"
import { useNoteDialog } from "@/components/notes/use-note-dialog"
import { LexicalPreview } from "@/components/tasks/lexical-preview"

const toLexical = (markdown?: string): LexicalText | undefined => {
  if (!markdown) return undefined
  const nodes = markdownToNodes(markdown) || undefined
  return { markdown, nodes, preferred_format: "markdown" }
}

interface CreateNoteProps {
  id: string
  householdId?: string
}

export default function CreateNote({ id, householdId: fallbackHouseholdId }: CreateNoteProps) {
  const { data: actionLink } = useActionLink(id)
  const { mutate: execute, isPending, isSuccess } = useExecuteActionLink()
  const { open } = useNoteDialog()

  const { content, householdId, contactId } = useMemo(() => {
    if (!actionLink?.properties) {
      return { content: undefined, householdId: fallbackHouseholdId, contactId: undefined }
    }

    const rawHouseholdId = actionLink.properties.household_id ?? fallbackHouseholdId
    const normalizedHouseholdId =
      typeof rawHouseholdId === "number"
        ? String(rawHouseholdId)
        : typeof rawHouseholdId === "string" && rawHouseholdId.length > 0
          ? rawHouseholdId
          : fallbackHouseholdId

    const rawContactId = actionLink.properties.contact_id
    const normalizedContactId =
      typeof rawContactId === "number"
        ? String(rawContactId)
        : typeof rawContactId === "string" && rawContactId.length > 0
          ? rawContactId
          : undefined

    const markdown =
      typeof actionLink.properties.content_markdown === "string" ? actionLink.properties.content_markdown : undefined

    return {
      content: toLexical(markdown),
      householdId: normalizedHouseholdId,
      contactId: normalizedContactId,
    }
  }, [actionLink, fallbackHouseholdId])

  if (!actionLink) return null

  const isExpired = new Date(actionLink.expires_at) < new Date()
  const isExecuted = !!actionLink.executed_at || isSuccess
  const isDisabled = isPending || isExpired || isExecuted

  const handleClick = () => {
    if (isDisabled) return

    open({
      noteId: "",
      householdId: householdId,
      contactId,
      prefillContent: content,
      onSaved: () => {
        execute({ id })
      },
    })
  }

  const label = (() => {
    if (isPending) return "Completing..."
    if (isExecuted) return "Note saved"
    if (isExpired) return "Note expired"
    return "Review & save note"
  })()

  const buttonVariant = isExecuted || isExpired ? "secondary" : "outline"

  const buttonClassName = cn(
    "transition-all",
    isExecuted && "cursor-default border-green-border bg-green-background text-green-foreground hover:bg-green-border",
    isExpired && "cursor-default border-tertiary bg-tinted text-muted-foreground hover:bg-tinted"
  )

  const icon = isExecuted ? <Check /> : <StickyNote />
  const helperText = (() => {
    if (isExecuted) return "Saved note from this suggestion."
    if (isExpired) return "This suggestion expired."
    return "Opens the note dialog so you can review and save."
  })()

  return (
    <Card className="mt-2 flex w-full max-w-md flex-col gap-2 p-4 shadow-sm">
      <div className="space-y-1">
        <h3 className="text-base font-medium">Suggested note</h3>
        {content ? (
          <LexicalPreview content={content} className="text-muted-foreground text-sm" />
        ) : (
          <p className="text-muted-foreground text-sm">Open to review and save this note.</p>
        )}
      </div>
      <Button variant={buttonVariant} onClick={handleClick} disabled={isDisabled} className={buttonClassName} size="sm">
        {icon}
        {label}
      </Button>
      <p className="text-muted-foreground text-xs">{helperText}</p>
    </Card>
  )
}
